import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

const CACHE_TTL_MS = 30_000;

interface LeaderboardEntry {
  username: string;
  total_games: number;
  wins: number;
  losses: number;
  ties: number;
  win_rate: number;
}

interface LeaderboardProps {
  socket: Socket | null;
  onClose: () => void;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({ socket, onClose }) => {
  const [rankings, setRankings] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [now, setNow] = useState<number>(Date.now());
  const cachedRef = useRef<LeaderboardEntry[]>([]);
  const lastFetchRef = useRef<number>(0);

  // Tick every second to keep "hace Xs" label fresh
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchData = useCallback((force = false) => {
    if (!socket) return;
    const age = Date.now() - lastFetchRef.current;
    if (!force && age < CACHE_TTL_MS && cachedRef.current.length > 0) {
      setRankings(cachedRef.current);
      setLoading(false);
      return;
    }
    setLoading(true);
    socket.emit('get-leaderboard');
  }, [socket]);

  useEffect(() => {
    if (!socket) return;

    socket.on('leaderboard-data', (data: LeaderboardEntry[]) => {
      cachedRef.current = data;
      lastFetchRef.current = Date.now();
      setRankings(data);
      setLastFetchTime(lastFetchRef.current);
      setLoading(false);
    });

    socket.on('leaderboard-error', (error: string) => {
      console.error('Error al cargar ranking:', error);
      setLoading(false);
    });

    fetchData();

    return () => {
      socket.off('leaderboard-data');
      socket.off('leaderboard-error');
    };
  }, [socket, fetchData]);

  const ageSeconds = lastFetchTime > 0 ? Math.floor((now - lastFetchTime) / 1000) : null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-retro-dark border-4 border-neon-cyan rounded-lg shadow-neon-cyan max-w-md w-full">
        {/* Header */}
        <div className="bg-neon-cyan/20 border-b-2 border-neon-cyan p-4 flex justify-between items-center gap-2">
          <h2 className="font-retro text-base sm:text-xl text-neon-cyan animate-glow leading-tight">
            🏆 RANKING GLOBAL
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            {ageSeconds !== null && !loading && (
              <span className="font-retro text-[8px] sm:text-xs text-neon-yellow opacity-70 whitespace-nowrap">
                hace {ageSeconds}s
              </span>
            )}
            <button
              onClick={() => fetchData(true)}
              disabled={loading}
              title="Actualizar ranking"
              className="ui-button text-neon-cyan hover:text-neon-yellow transition-colors text-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? '⏳' : '🔄'}
            </button>
            <button
              onClick={onClose}
              className="ui-button text-neon-pink hover:text-neon-yellow transition-colors text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8">
              <p className="font-retro text-neon-cyan animate-pulse text-sm">
                CARGANDO RANKING...
              </p>
            </div>
          ) : rankings.length === 0 ? (
            <div className="text-center py-8">
              <p className="font-retro text-neon-yellow text-sm">
                NO HAY DATOS AÚN
              </p>
              <p className="font-retro text-neon-cyan text-xs mt-2">
                ¡Sé el primero en jugar!
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table Header */}
              <div className="grid grid-cols-6 gap-2 font-retro text-neon-cyan text-xs border-b-2 border-neon-cyan pb-2">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-2">JUGADOR</div>
                <div className="text-center">V</div>
                <div className="text-center">D</div>
                <div className="text-center">%</div>
              </div>

              {/* Rankings */}
              {rankings.map((entry, index) => (
                <div
                  key={entry.username}
                  className={`grid grid-cols-6 gap-2 font-retro text-xs sm:text-sm p-2 rounded transition-all ${
                    index === 0
                      ? 'bg-neon-yellow/20 text-neon-yellow border-2 border-neon-yellow'
                      : index === 1
                      ? 'bg-gray-400/20 text-gray-300 border-2 border-gray-400'
                      : index === 2
                      ? 'bg-orange-600/20 text-orange-400 border-2 border-orange-600'
                      : 'bg-neon-cyan/10 text-neon-cyan hover:bg-neon-cyan/20'
                  }`}
                >
                  <div className="col-span-1 text-center font-bold">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                  </div>
                  <div className="col-span-2 truncate">{entry.username}</div>
                  <div className="text-center text-neon-green">{entry.wins}</div>
                  <div className="text-center text-neon-pink">{entry.losses}</div>
                  <div className="text-center font-bold">{Number(entry.win_rate).toFixed(1)}%</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-neon-cyan/20 border-t-2 border-neon-cyan p-4 text-center">
          <button
            onClick={onClose}
            className="ui-button bg-neon-pink/30 hover:bg-neon-pink/50 border-2 border-neon-pink text-neon-pink hover:shadow-neon-pink text-xs sm:text-sm"
          >
            CERRAR
          </button>
        </div>
      </div>
    </div>
  );
};
